import * as Y from 'yjs'
import { api, ErroreApi } from './api.ts'
import type { AllegatoDaInviare, Messaggio, ScadenzaDaInviare } from './posta.ts'

/**
 * Bozze di email scritte a più mani, in contemporanea (505).
 *
 * Il contenuto è un documento Yjs: cinque testi condivisi (a, cc, ccn, oggetto, testo) e l'elenco
 * degli allegati. Il compositore lega i campi ai testi (pagine/posta/Compositore.tsx); qui sta il
 * collegamento con l'API, che è solo HTTP:
 *  - le modifiche locali si raccolgono e partono al massimo ogni 750 ms, fuse in un aggiornamento;
 *  - quelle altrui arrivano da una richiesta in attesa (fino a 20 secondi) dal numero di sequenza
 *    già ricevuto, rifatta appena torna, al massimo una al secondo;
 *  - la presenza è un battito ogni cinque secondi, con il campo in cui si sta scrivendo.
 * Attesa e battito non rinnovano la sessione (plugin/sessione.ts dell'API): una bozza lasciata aperta
 * non tiene vivo l'accesso. Scrivere sì.
 *
 * Uscita dalla pagina (3/11/2026): quando la scheda si nasconde (`visibilitychange`, il telefono che passa a
 * un'altra app) le modifiche in coda partono subito, con `keepalive`; quando la pagina se ne va (`pagehide`)
 * partono anche quelle in viaggio, che il browser interromperebbe. Il compositore chiede conferma prima di
 * lasciare la pagina se qualcosa non è ancora salvato (`daSalvare`).
 *
 * Le origini delle transazioni Yjs dicono da dove viene una modifica: LOCALE è chi scrive in un campo,
 * PROGRAMMA è il compositore (la firma che si aggiunge o si toglie), REMOTA arriva dal server e non
 * torna indietro.
 */

export const ORIGINE_LOCALE = 'locale'
export const ORIGINE_PROGRAMMA = 'programma'
export const ORIGINE_REMOTA = 'remota'

export const CAMPI_TESTO = ['a', 'cc', 'ccn', 'oggetto', 'testo'] as const
export type CampoTesto = (typeof CAMPI_TESTO)[number]
export type Campo = CampoTesto | 'allegati'
export type StatoBozza = 'aperta' | 'in_invio' | 'inviata'

export type Persona = { id: string; nome: string; cognome: string }
export type Presente = Persona & { campo: Campo | null }
export type Invito = { utente: Persona; invitato_da: Persona | null; creato_il: string }

export type StatoIniziale = {
  bozza: {
    id: string
    casella: { id: string; indirizzo: string; nome_visualizzato: string; ambito: string }
    stato: StatoBozza
    in_risposta_a: string | null
    creata_da: Persona | null
    creata_il: string
    modificata_il: string
    inviata_da: Persona | null
    inviata_il: string | null
  }
  /** Scrive dalla casella: spedisce, elimina, invita. Falso per chi entra con un invito. */
  scrive: boolean
  seq: number
  /** Stato Yjs completo in base64; null sulla bozza inviata, che non ha più contenuto. */
  stato_yjs: string | null
  /** Il messaggio a cui la bozza risponde; all'invitato senza allegati. */
  risposta_a: Messaggio | null
  presenti: Presente[]
  presenza: string
  inviti: Invito[] | null
  /** Per chi entra con un invito: chi l'ha invitato. */
  invitata_da: Persona | null
  /** L'ultimo invio non è riuscito (3/11/2026): il motivo, e quando. */
  non_inviato?: { errore: string | null; il: string } | null
}

type Novita = {
  seq: number
  istantanea: string | null
  aggiornamenti: { seq: number; dati: string }[]
  stato: StatoBozza
  inviata_da: Persona | null
  presenti: Presente[]
  presenza: string
}

export type VoceBozza = {
  id: string
  casella_id: string
  casella_indirizzo: string
  oggetto: string
  a: string
  risposta: boolean
  stato: StatoBozza
  modificata_il: string
  creata_da: Persona | null
  presenti: Presente[]
  invitati: number | null
  invitata_da: Persona | null
}

export type ElencoBozze = { mie: VoceBozza[]; condivise: VoceBozza[] }

export type EsitoInvio = { id: string; thread_id?: string; stato_invio: 'inviato' | 'errore'; errore: string | null }

// --- chiamate ----------------------------------------------------------------------------------

/** Le bozze in corso: delle caselle da cui scrivi, e quelle condivise con te. */
export const elencaBozze = (automatica = false) => api.get<ElencoBozze>('/posta/bozze', { automatica })

export const creaBozza = (dati: { casella_id: string; in_risposta_a?: string; stato: string }) =>
  api.post<StatoIniziale>('/posta/bozze', dati)

export const apriBozza = (id: string) => api.get<StatoIniziale>(`/posta/bozze/${id}`)

/** La scadenza (510) la sceglie chi preme «Invia»: non sta nel documento condiviso. */
/** Scadenza (510), conferma di lettura e priorita' (539): le sceglie chi preme «Invia». */
export const inviaBozza = (id: string, scadenza?: ScadenzaDaInviare | null,
  opzioni: { conferma_lettura?: boolean; priorita_alta?: boolean } = {}) =>
  api.post<EsitoInvio>(`/posta/bozze/${id}/invia`, {
    ...(scadenza ? { scadenza } : {}),
    ...(opzioni.conferma_lettura ? { conferma_lettura: true } : {}),
    ...(opzioni.priorita_alta ? { priorita_alta: true } : {}),
  })

export const eliminaBozza = (id: string) => api.elimina<{ eliminata: boolean }>(`/posta/bozze/${id}`)

export const spostaBozza = (id: string, casellaId: string) =>
  api.put<{ casella_id: string }>(`/posta/bozze/${id}/casella`, { casella_id: casellaId })

export const elencaInvitabili = (id: string) =>
  api.get<(Persona & { email: string })[]>(`/posta/bozze/${id}/invitabili`)

export const invita = (id: string, utenteId: string) =>
  api.post<Invito[]>(`/posta/bozze/${id}/inviti`, { utente_id: utenteId })

export const revocaInvito = (id: string, utenteId: string) =>
  api.elimina<Invito[]>(`/posta/bozze/${id}/inviti/${utenteId}`)

// --- documento ---------------------------------------------------------------------------------

/** Documento vuoto con i tipi già dichiarati, come lo crea l'API. */
export function nuovoDocumento(): Y.Doc {
  const doc = new Y.Doc()
  for (const campo of CAMPI_TESTO) doc.getText(campo)
  doc.getArray<AllegatoDaInviare>('allegati')
  return doc
}

export const allegatiDi = (doc: Y.Doc) => doc.getArray<AllegatoDaInviare>('allegati')

export function inBase64(dati: Uint8Array): string {
  let binario = ''
  for (let i = 0; i < dati.length; i += 0x8000) {
    binario += String.fromCharCode(...dati.subarray(i, i + 0x8000))
  }
  return btoa(binario)
}

export function daBase64(testo: string): Uint8Array {
  const binario = atob(testo)
  const dati = new Uint8Array(binario.length)
  for (let i = 0; i < binario.length; i += 1) dati[i] = binario.charCodeAt(i)
  return dati
}

// --- legame fra un campo e un testo condiviso ----------------------------------------------------

/**
 * La modifica minima che porta `vecchio` a `nuovo`: dove, quanti caratteri togliere, che cosa
 * inserire. Il prefisso comune si ferma al cursore: scrivendo una «a» dentro «aa» il testo non dice
 * quale delle due è nuova, la posizione del cursore sì.
 */
export function differenza(
  vecchio: string, nuovo: string, cursore: number,
): { da: number; togli: number; metti: string } | null {
  if (vecchio === nuovo) return null
  const crescita = nuovo.length - vecchio.length
  const limite = Math.max(0, Math.min(vecchio.length, nuovo.length, crescita > 0 ? cursore - crescita : cursore))
  let da = 0
  while (da < limite && vecchio[da] === nuovo[da]) da += 1
  let coda = 0
  const massimaCoda = Math.min(vecchio.length, nuovo.length) - da
  while (coda < massimaCoda && vecchio[vecchio.length - 1 - coda] === nuovo[nuovo.length - 1 - coda]) coda += 1
  return { da, togli: vecchio.length - da - coda, metti: nuovo.slice(da, nuovo.length - coda) }
}

type Delta = { insert?: unknown; retain?: number; delete?: number }[]

/**
 * Dove finisce l'indice `indice` del testo dopo una modifica altrui descritta da `delta`. Un
 * inserimento proprio nel punto del cursore lo sposta avanti, come nei documenti condivisi: il testo
 * dell'altro compare prima del cursore, e chi scrive continua dopo.
 */
export function trasforma(indice: number, delta: Delta): number {
  let posizione = 0
  let risultato = indice
  for (const op of delta) {
    if (posizione > indice) break
    if (op.retain) posizione += op.retain
    else if (op.insert !== undefined) {
      risultato += typeof op.insert === 'string' ? op.insert.length : 1
    } else if (op.delete) {
      risultato -= Math.min(op.delete, indice - posizione)
      posizione += op.delete
    }
  }
  return Math.max(0, risultato)
}

// --- sessione di scrittura ---------------------------------------------------------------------

const pausa = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** Ritardo con cui partono le modifiche locali, raccolte in un solo aggiornamento. */
const RITARDO_INVIO_MS = 750
/** Battito di presenza. L'API considera presente chi ha battuto da meno di 15 secondi. */
const BATTITO_MS = 5000
/** Al massimo una richiesta in attesa al secondo, anche quando le modifiche arrivano fitte. */
const INTERVALLO_MINIMO_MS = 1000
/** Oltre questo corpo `keepalive` non è ammesso dal browser (64 KB in tutto): la modifica parte normale. */
const KEEPALIVE_MASSIMO = 60_000

/**
 * Una bozza aperta nel compositore e collegata al server. Si crea dallo stato iniziale (apertura o
 * creazione), si avvia, e si chiude quando il compositore si chiude.
 *
 * `ascolta` avvisa a ogni cambiamento di ciò che l'interfaccia mostra: stato, chi c'è, salvataggio,
 * problemi. Il contenuto lo dice il documento, che il compositore osserva da sé.
 */
export class SessioneBozza {
  readonly id: string
  readonly doc: Y.Doc
  seq: number
  stato: StatoBozza
  inviataDa: Persona | null
  presenti: Presente[]
  presenza: string
  /** Modifiche locali non ancora sul server. */
  inSospeso = 0
  salvataIl: Date | null = null
  problemaSalvataggio: string | null = null
  problemaRicezione: string | null = null
  /** La bozza non c'è più, o non è più tua da aprire (404): eliminata, invito revocato. */
  persa = false

  #coda: Uint8Array[] = []
  /** Il blocco che sta viaggiando verso il server: all'uscita dalla pagina riparte con `keepalive`. */
  #inViaggio: Uint8Array | null = null
  /** La scheda è nascosta: le modifiche partono con `keepalive`, che sopravvive alla pagina. */
  #nascosta = false
  #invio: Promise<void> | null = null
  #timerInvio: ReturnType<typeof setTimeout> | undefined
  #timerBattito: ReturnType<typeof setInterval> | undefined
  #campo: Campo | null = null
  #vivo = true
  #controllore = new AbortController()
  #ascoltatori = new Set<() => void>()

  constructor(doc: Y.Doc, s: Pick<StatoIniziale, 'seq' | 'presenti' | 'presenza'> & {
    id: string; stato: StatoBozza; inviataDa: Persona | null
  }) {
    this.id = s.id
    this.doc = doc
    this.seq = s.seq
    this.stato = s.stato
    this.inviataDa = s.inviataDa
    this.presenti = s.presenti
    this.presenza = s.presenza
    this.doc.on('update', this.#suAggiornamento)
    window.addEventListener('pagehide', this.#allUscita)
    document.addEventListener('visibilitychange', this.#suVisibilita)
  }

  /** Qualcosa non è ancora sul server: modifiche in coda o in viaggio. Il compositore chiede conferma all'uscita. */
  get daSalvare(): boolean {
    return this.#vivo && this.stato !== 'inviata' && (this.#coda.length > 0 || this.#invio !== null)
  }

  avvia(): void {
    if (this.stato === 'inviata') return
    void this.#ciclo()
    void this.#batti()
    this.#timerBattito = setInterval(() => void this.#batti(), BATTITO_MS)
  }

  ascolta(funzione: () => void): () => void {
    this.#ascoltatori.add(funzione)
    return () => { this.#ascoltatori.delete(funzione) }
  }

  #avvisa(): void {
    this.inSospeso = this.#coda.length + (this.#invio ? 1 : 0)
    for (const f of this.#ascoltatori) f()
  }

  /** Aggiornamenti prodotti prima che la sessione esistesse: alla creazione, quelli scritti nel frattempo. */
  accoda(aggiornamento: Uint8Array): void {
    this.#coda.push(aggiornamento)
    this.#avvisa()
    this.#programmaInvio()
  }

  #suAggiornamento = (aggiornamento: Uint8Array, origine: unknown) => {
    if (origine === ORIGINE_REMOTA || !this.#vivo) return
    this.accoda(aggiornamento)
  }

  #programmaInvio(ritardo = RITARDO_INVIO_MS): void {
    if (this.#timerInvio || this.#invio) return
    this.#timerInvio = setTimeout(() => {
      this.#timerInvio = undefined
      void this.#svuota()
    }, ritardo)
  }

  /**
   * Manda le modifiche in coda. Un errore le rimette in testa alla coda e rimanda il tentativo di
   * qualche secondo: senza rete la bozza resta modificabile, e le modifiche partono quando torna.
   */
  #svuota(): Promise<void> {
    if (this.#invio) return this.#invio
    let ritardo = RITARDO_INVIO_MS
    this.#invio = (async () => {
      while (this.#coda.length && this.#vivo && this.stato !== 'inviata') {
        const blocco = this.#coda.splice(0)
        const dati = blocco.length === 1 ? blocco[0]! : Y.mergeUpdates(blocco)
        this.#inViaggio = dati
        try {
          const corpo = { dati: inBase64(dati) }
          await api.post(`/posta/bozze/${this.id}/aggiornamenti`, corpo,
            this.#nascosta && corpo.dati.length < KEEPALIVE_MASSIMO ? { keepalive: true } : undefined)
          this.problemaSalvataggio = null
          this.salvataIl = new Date()
        } catch (e) {
          if (e instanceof ErroreApi && e.stato === 404) { this.#perdi(); return }
          if (e instanceof ErroreApi && e.codice === 'BOZZA_INVIATA') {
            // Spedita da un altro mentre si scriveva: la modifica non entra più. Lo stato e chi l'ha
            // inviata arrivano dalla richiesta in attesa.
            this.problemaSalvataggio = e.message
            return
          }
          this.#coda.unshift(dati)
          this.problemaSalvataggio = e instanceof ErroreApi && e.codice === 'INVIO_IN_CORSO'
            ? e.message
            : 'Modifiche non ancora salvate: riprovo fra poco.'
          ritardo = 3000
          return
        } finally {
          this.#inViaggio = null
          this.#avvisa()
        }
      }
    })().finally(() => {
      this.#invio = null
      this.#avvisa()
      if (this.#coda.length && this.#vivo && this.stato !== 'inviata') this.#programmaInvio(ritardo)
    })
    this.#avvisa()
    return this.#invio
  }

  /** Aspetta che le modifiche locali siano tutte sul server: prima di «Invia» e di «Chiudi». */
  async attendiSalvataggio(): Promise<boolean> {
    for (let giri = 0; giri < 3 && (this.#coda.length || this.#invio); giri += 1) {
      clearTimeout(this.#timerInvio)
      this.#timerInvio = undefined
      await this.#svuota()
    }
    return this.#coda.length === 0 && !this.problemaSalvataggio
  }

  async #ciclo(): Promise<void> {
    let errori = 0
    while (this.#vivo && this.stato !== 'inviata') {
      const inizio = Date.now()
      try {
        const q = new URLSearchParams({ dal: String(this.seq), stato: this.stato, presenza: this.presenza })
        const n = await api.get<Novita>(`/posta/bozze/${this.id}/aggiornamenti?${q}`,
          { segnale: this.#controllore.signal })
        if (!this.#vivo) return
        this.#applica(n)
        errori = 0
        if (this.problemaRicezione) { this.problemaRicezione = null; this.#avvisa() }
      } catch (e) {
        if (!this.#vivo) return
        if (e instanceof ErroreApi && (e.stato === 404 || e.stato === 401 || e.codice === 'INTERRUTTORE_SPENTO')) {
          this.#perdi()
          return
        }
        errori += 1
        this.problemaRicezione = 'Collegamento interrotto: le modifiche degli altri arriveranno appena torna.'
        this.#avvisa()
        await pausa(Math.min(1000 * 2 ** errori, 15_000))
      }
      const trascorso = Date.now() - inizio
      if (trascorso < INTERVALLO_MINIMO_MS) await pausa(INTERVALLO_MINIMO_MS - trascorso)
    }
  }

  #applica(n: Novita): void {
    if (n.istantanea) Y.applyUpdate(this.doc, daBase64(n.istantanea), ORIGINE_REMOTA)
    for (const a of n.aggiornamenti) Y.applyUpdate(this.doc, daBase64(a.dati), ORIGINE_REMOTA)
    this.seq = n.seq
    this.stato = n.stato
    this.inviataDa = n.inviata_da
    this.presenti = n.presenti
    this.presenza = n.presenza
    if (n.stato === 'inviata') this.#ferma()
    this.#avvisa()
  }

  /** Il campo in cui si sta scrivendo: lo dice subito agli altri, senza aspettare il battito. */
  campo(campo: Campo | null): void {
    if (campo === this.#campo) return
    this.#campo = campo
    void this.#batti()
  }

  async #batti(): Promise<void> {
    if (!this.#vivo || this.stato === 'inviata') return
    try {
      await api.post(`/posta/bozze/${this.id}/presenza`, { campo: this.#campo })
      // Il battito e' passato: il collegamento c'e' anche se l'attesa in corso non e' ancora tornata.
      if (this.problemaRicezione) { this.problemaRicezione = null; this.#avvisa() }
    } catch { /* il battito successivo riprova */ }
  }

  #ferma(): void {
    clearInterval(this.#timerBattito)
    clearTimeout(this.#timerInvio)
    this.#timerInvio = undefined
    this.#controllore.abort()
  }

  #perdi(): void {
    this.persa = true
    this.#vivo = false
    this.#ferma()
    this.#avvisa()
  }

  /**
   * Chiusura del compositore, o della pagina: le modifiche rimaste partono comunque, e la presenza
   * sparisce subito invece che dopo quindici secondi. Con `keepalive` la richiesta sopravvive alla
   * pagina che se ne va.
   */
  chiudi(): void {
    if (!this.#vivo && this.persa) return
    // Anche il blocco in viaggio: la pagina che se ne va interrompe la sua richiesta. Rimandato due volte, un
    // aggiornamento Yjs non cambia niente.
    const rimaste = [...(this.#inViaggio ? [this.#inViaggio] : []), ...this.#coda.splice(0)]
    const restava = this.#vivo && this.stato !== 'inviata'
    this.#vivo = false
    this.#ferma()
    this.doc.off('update', this.#suAggiornamento)
    window.removeEventListener('pagehide', this.#allUscita)
    document.removeEventListener('visibilitychange', this.#suVisibilita)
    if (!restava) return
    const invia = (percorso: string, corpo: unknown) => {
      void fetch(`/api/posta/bozze/${this.id}/${percorso}`, {
        method: 'POST', keepalive: true, credentials: 'include',
        headers: { 'content-type': 'application/json' }, body: JSON.stringify(corpo),
      }).catch(() => undefined)
    }
    if (rimaste.length) invia('aggiornamenti', { dati: inBase64(rimaste.length === 1 ? rimaste[0]! : Y.mergeUpdates(rimaste)) })
    invia('presenza', { via: true })
  }

  /**
   * La pagina se ne va. Se finisce nella cache del browser (`persisted`, si torna indietro e la si ritrova) la
   * sessione resta: le modifiche partono e basta. Altrimenti si chiude, come alla chiusura del compositore.
   */
  #allUscita = (e: PageTransitionEvent) => {
    if (!e.persisted) { this.chiudi(); return }
    this.#nascosta = true
    void this.#svuotaSubito()
  }

  /** La scheda si nasconde: le modifiche in coda partono subito, con `keepalive`; torna visibile, come prima. */
  #suVisibilita = () => {
    this.#nascosta = document.visibilityState === 'hidden'
    if (this.#nascosta) void this.#svuotaSubito()
  }

  #svuotaSubito(): Promise<void> {
    if (!this.#vivo || this.stato === 'inviata' || !this.#coda.length) return Promise.resolve()
    clearTimeout(this.#timerInvio)
    this.#timerInvio = undefined
    return this.#svuota()
  }
}

/** «Marco Conti (oggetto)»: chi c'è e dove scrive, per la riga sopra il compositore. */
const NOMI_CAMPI: Record<Campo, string> = {
  a: 'destinatari', cc: 'copia', ccn: 'copia nascosta', oggetto: 'oggetto', testo: 'testo', allegati: 'allegati',
}

export function descriviPresenti(presenti: Presente[]): string {
  return presenti.map((p) => `${p.nome} ${p.cognome}${p.campo ? ` (${NOMI_CAMPI[p.campo]})` : ''}`).join(', ')
}
